import React, { FC, useEffect, useState } from 'react';
import { useDispatch } from "react-redux";
import { CloneButton, EditButton, useDataProvider, useNotify, usePermissions } from 'react-admin';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import DoneOutlineIcon from '@material-ui/icons/DoneOutline';

import { Card, TableContainer, Table, TableHead, TableRow, TableCell, Box, TableBody, Dialog, DialogContent } from '@material-ui/core';
import { FixedOdds } from '../../types/fixedOdds';
import HeadTable from '../common/sortTable/sortTable';
import { removeMark, setMark } from '../../redux/actions/propertyActions';
import DiscountFilter from './DiscountFilter';
import FilterComponent from '../common/tablesFilter/FilterList';
import { searchSelect, selectedAfterSearch, selectedFilterItems, selectSelectedFilter } from '../../helpers/filter';
import PrizeListTitle from '../common/listTitles/PrizeListTitle';
import { getFieldByTitle } from '../../helpers/sortByTitle';
import PaginationTable from '../common/paginationTable';
import { checkPermission } from '../../helpers/checkPermissions';
import RemoveItem from '../common/Remove/RemoveItem';
import DateFilter from '../users/DateFilter';
import { FilterDate } from '../../types/commonForm';
import { getDate } from '../../helpers/dateHelper';


interface State {
    fixedOdds: Array<FixedOdds>;
    total: number;
    page: number;
    perPage: number;
    field: string;
    order: string;
    status:string;
    endDate: FilterDate;
}

const itemsForFilter = (data: Array<FixedOdds>) => {
    const titles = Array<FilterFixedOdds>()
    const ids = Array<FilterFixedOdds>()

    data.map((item) => {
        const titleItem: FilterFixedOdds = {
            id: item._id || '',
            title: item.title
        }
        const idItem: FilterFixedOdds = {
            id: item._id || '',
            title: item._id || ''
        }
        titles.push(titleItem)
        ids.push(idItem)
    })
    return { titles, ids }
}

interface FilterFixedOdds {
    id: string;
    title: string;
}

const FixedOddsList: FC = () => {
    const { permissions } = usePermissions()
    const dataProvider = useDataProvider()
    const dispatch = useDispatch();
    const notify = useNotify()
    const [state, setState] = useState<State>({
        fixedOdds: Array<FixedOdds>(),
        total: 0,
        page: 1,
        perPage: 10,
        status: "All",
        field: '_id',
        order: 'asc',
        endDate: {
            startDate: undefined,
            endDate: undefined
        }
    })

    const [dropdown, setDropdown] = useState({
        id: false,
        title: false,
        discount: false,
        start:false,
        finish:false
    })

    const [searchState, setSearchState] = useState({
        id: '',
        title: '',
    })

    const [filter, setFilter] = useState({
        discount: 'all',
        titles: Array<FilterFixedOdds>(),
        ids: Array<FilterFixedOdds>(),
        selectedTitles: Array<string>(),
        selectedIds: Array<string>(),
        titlesForSearch: Array<FilterFixedOdds>(),
        idsForSearch: Array<FilterFixedOdds>()
    })

    const [discountFilter, setDiscountFilter] = useState({
        with: false,
        without: false
    })

    const [modal, setModal] = useState({
        showModal: false,
        id: ''
    })

    const statusFilter = (status: string) => {
        getFixedOdds(1, state.perPage, status, state.field, state.order, filter.discount, filter.selectedIds, filter.selectedTitles,state.endDate)
    }

    const markClone = () => {
        dispatch(setMark())
    }

    const applyDateFilter = () => {
        getFixedOdds(1, state.perPage, state.status,state.field, state.order, filter.discount, filter.selectedIds, filter.selectedTitles, state.endDate)
    }

    useEffect(() => {
        dispatch(removeMark())
        getFixedOdds(1, 10, "All", '_id', 'asc', 'all', Array<string>(), Array<string>(),state.endDate)
        getFixedOddsForFilter('all', Array<string>(), Array<string>())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const getFixedOdds = async (page: number, perPage: number, status: string, field: string, order: string, discount: string, ids: Array<string>, titles: Array<string>, endDate) => {
        const result = await dataProvider.getList('fixedOdds', {
            filter: { status: status, discount: discount, ids: ids, titles: titles, endsAt:endDate },
            pagination: { page: page, perPage: perPage },
            sort: { field: field, order: order },
        }) as any
        setState({
            ...state,
            fixedOdds: result.data,
            total: result.total,
            page: page,
            perPage: perPage,
            status: status,
            field: field,
            order: order.toLowerCase()
        })
    }

    const getFixedOddsForFilter = async (discount: string, id: Array<string>, title: Array<string>) => {
        const fixedOdds = await dataProvider.getList('fixedOdds', {
            filter: { status: "All", discount: discount, ids: id, titles: title },
            pagination: { page: 1, perPage: 1000 },
            sort: { field: "_id", order: "asc" },
        }) as any
        const { ids, titles } = itemsForFilter(fixedOdds.data)
        const selectedIds = selectSelectedFilter(filter.selectedIds, ids)
        const selectedTitles = selectSelectedFilter(filter.selectedTitles, titles)
        setFilter({
            ...filter,
            discount: discount,
            titles: titles,
            ids: ids,
            titlesForSearch: titles,
            idsForSearch: ids,
            selectedIds: selectedIds,
            selectedTitles: selectedTitles
        })
        return fixedOdds
    }

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
        getFixedOdds(newPage + 1, state.perPage, state.status, state.field, state.order, filter.discount, filter.selectedIds, filter.selectedTitles,state.endDate)
    };

    const handleLastPage = () => {
        const page = Math.ceil(state.total / state.perPage)
        getFixedOdds(page, state.perPage, state.status, state.field, state.order, filter.discount, filter.selectedIds, filter.selectedTitles,state.endDate)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        const perPage = parseInt(event.target.value, 10)
        getFixedOdds(state.page, perPage, state.status, state.field, state.order, filter.discount, filter.selectedIds, filter.selectedTitles,state.endDate)
    }

    const handleFirstPage = () => {
        getFixedOdds(1, state.perPage, state.status, state.field, state.order, filter.discount, filter.selectedIds, filter.selectedTitles,state.endDate)
    }

    const changeStatus = async (fixedOdd: FixedOdds) => {
        if (checkPermission(permissions, "Fixed Odds", "Update")) {
            fixedOdd.active = !fixedOdd.active
            fixedOdd.isStatus = true
           const result = await dataProvider.update("fixedOdds", {
                data: fixedOdd,
                id: fixedOdd.id,
                previousData: {} as any
            });
            if (result.data.result.active && result.data.result.isTrending) {
                await dataProvider.addFeatured("FIXED_ODDS",result.data.result._id)
            } else {
                await dataProvider.removeFeatured("FIXED_ODDS",result.data.result._id)
            }
            getFixedOdds(state.page, state.perPage, state.status, state.field, state.order, filter.discount, filter.selectedIds, filter.selectedTitles, state.endDate)
        }
    }

    const handleSort = (direction: string, field: string) => {
        field = getFieldByTitle(field)
        getFixedOdds(state.page, state.perPage, state.status, field, direction.toLowerCase(), filter.discount, filter.selectedIds, filter.selectedTitles,state.endDate)
    }

    const filterDropdown = (title: string) => {
        title = title === "#" ? 'id' : title.toLowerCase()
        setDropdown({ ...dropdown, [title]: !dropdown[title] })
    }

    const applyDiscountFilter = async () => {
        let discount = ''
        if (discountFilter.with && discountFilter.without || !discountFilter.with && !discountFilter.without) {
            discount = 'all'
        }
        if (discountFilter.with && !discountFilter.without) {
            discount = 'with'
        }
        if (!discountFilter.with && discountFilter.without) {
            discount = 'without'
        }
        setFilter({ ...filter, discount: discount })
        getFixedOdds(state.page, state.perPage, state.status, state.field, state.order, discount, filter.selectedIds, filter.selectedTitles,state.endDate)
        getFixedOddsForFilter(discount, filter.selectedIds, filter.selectedTitles)
    }

    const applyFilter = () => {
        getFixedOdds(state.page, state.perPage, state.status, state.field, state.order, filter.discount, filter.selectedIds, filter.selectedTitles,state.endDate)
        getFixedOddsForFilter(filter.discount, filter.selectedIds, filter.selectedTitles)
        setSearchState({ id: '', title: '' })
    }


    const checkDiscountFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
        const discount = discountFilter
        discount[event.target.name] = event.target.checked
        let currentDiscount = ''
        if (discount.with && discount.without || !discount.with && !discount.without) {
            currentDiscount = 'all'
        }
        if (discount.with && !discount.without) {
            currentDiscount = 'with'
        }
        if (!discount.with && discount.without) {
            currentDiscount = 'without'
        }
        setFilter({ ...filter, discount: currentDiscount })
        setDiscountFilter(discount)
    }

    const clearDiscountFilter = () => {
        setDiscountFilter({ with: false, without: false })
        setFilter({ ...filter, discount: "all" })
    }

    const selectedId = (id: string, checked: boolean) => {
        const ids = selectedFilterItems(id, checked, filter.selectedIds)
        setFilter({ ...filter, selectedIds: ids })
    }

    const selectedTitle = (id: string, checked: boolean) => {
        const titles = selectedFilterItems(id, checked, filter.selectedTitles)
        setFilter({ ...filter, selectedTitles: titles })
    }

    const searchIds = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchState({ ...searchState, id: event.currentTarget.value })
        const ids = searchSelect(event.currentTarget.value, filter.idsForSearch)
        const selectedIds = selectedAfterSearch(ids, filter.selectedIds)
        setFilter({ ...filter, ids: ids, selectedIds: selectedIds })
    }

    const searchTitles = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchState({ ...searchState, title: event.currentTarget.value })
        const titles = searchSelect(event.currentTarget.value, filter.titlesForSearch)
        const selectedTitles = selectedAfterSearch(titles, filter.selectedTitles)
        setFilter({ ...filter, titles: titles, selectedTitles: selectedTitles })
    }

    
    const clearEndDateFilter = () => {
        const endDateObject = { startDate: undefined, endDate: undefined }
        setState({ ...state, endDate: endDateObject })
    }

    const clearSelectedFilter = (clear: string) => {
        setFilter({ ...filter, [clear]: Array<string>() })
    }

    const setEndStartDate = (value: Date) => {
        const endDateObject = state.endDate
        endDateObject.startDate = value
        setState({ ...state, endDate: endDateObject })
    }
    
    const setEndEndDate = (value: Date) => {
        const endDateObject = state.endDate
        endDateObject.endDate = value
        setState({ ...state, endDate: endDateObject })
    }

    const selectAllIds = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked
        let ids = filter.ids
        if (checked) {
            const selectedIds = ids.reduce((acc, item) => {
                acc.push(item.id)
                return acc
            }, Array<string>())
            setFilter({ ...filter, selectedIds: selectedIds })
        } else {
            setFilter({ ...filter, selectedIds: Array<string>() })
        }
    }

    const selectedAllTitles = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked
        let titles = filter.titles
        if (checked) {
            const selectedTitles = titles.reduce((acc, item) => {
                acc.push(item.id)
                return acc
            }, Array<string>())
            setFilter({ ...filter, selectedTitles: selectedTitles })
        } else {
            setFilter({ ...filter, selectedTitles: Array<string>() })
        }
    }

    const closeFilter = () => {
        setDropdown({
            id: false,
            title: false,
            discount: false,
            start:false,
            finish:false
        })
    }

    const closeModal = (): void => {
        setModal({ ...modal, showModal: false, id: '' })
    }

    const openModal = (id: string | undefined): void => {
        if (id) {
            setModal({ ...modal, showModal: true, id: id })
        }
    }

    const removeFixedOdds = async (): Promise<void> => {
        try {
            const result = await dataProvider.delete('fixedOdds', {
                id: modal.id,
                previousData: {} as FixedOdds
            })
            if (result.data && result.data.message) {
                closeModal()
                await dataProvider.removeFeatured("FIXED_ODDS",modal.id)
                getFixedOdds(state.page, state.perPage, state.status, state.field, state.order, filter.discount, filter.selectedIds, filter.selectedTitles,state.endDate)
                notify(result.data.message, "info")
            }
        } catch (error) {
            notify("FixedOdds not found", "error")
        }
    }

    return (
        <>
            <PrizeListTitle
                activeFilter={statusFilter}
                title="Fixed Odds"
                path="fixedOdds"
                isPrize={false}
                permission={checkPermission(permissions, "Fixed Odds", "Update")}
            />
            <Card className="prize-table-card">
                <TableContainer className="fixedOddsTable">
                    <Table aria-label="simple table">
                        <TableHead>
                            <TableRow className="order-row">
                                <TableCell id="active-table-head" align="left">
                                    <HeadTable title="#" sort={handleSort} filterHandle={filterDropdown} />
                                    {dropdown.id
                                        &&
                                        <FilterComponent
                                            categories={filter.ids}
                                            search={searchIds}
                                            selectedCategories={filter.selectedIds}
                                            handleSelectedCategory={selectedId}
                                            searchString={searchState.id}
                                            applyCategoryFilter={applyFilter}
                                            clearCategory={clearSelectedFilter}
                                            selectedAllCategories={selectAllIds}
                                            appointment="selectedIds"
                                        />
                                    }
                                </TableCell>
                                <TableCell id="active-table-head" align="left">
                                    <HeadTable title="Title" sort={handleSort} filterHandle={filterDropdown} />
                                    {dropdown.title
                                        &&
                                        <FilterComponent
                                            categories={filter.titles}
                                            search={searchTitles}
                                            selectedCategories={filter.selectedTitles}
                                            handleSelectedCategory={selectedTitle}
                                            searchString={searchState.title}
                                            applyCategoryFilter={applyFilter}
                                            clearCategory={clearSelectedFilter}
                                            selectedAllCategories={selectedAllTitles}
                                            appointment="selectedTitles"
                                        />
                                    }
                                </TableCell>
                                <TableCell id="active-table-head" align="left">
                                    <HeadTable title="Price" sort={handleSort}/>
                                </TableCell>
                                <TableCell id="discount-table-head" align="left">
                                    <HeadTable title="Discount" sort={handleSort} filterHandle={filterDropdown} />
                                    {dropdown.discount
                                        &&
                                        <DiscountFilter
                                            applyFilter={applyDiscountFilter}
                                            checkDiscountFilter={checkDiscountFilter}
                                            clearSelectedItem={clearDiscountFilter}
                                            withDiscount={discountFilter.with}
                                            withoutDiscount={discountFilter.without}
                                            firstCheckboxTitle="with discount"
                                            secondCheckboxTitle="without discount"
                                        />
                                    }
                                </TableCell>
                                <TableCell id="active-table-head" align="left">
                                    <Box display="flex" justifyContent="center">
                                        Number of tickets
                                    </Box>
                                </TableCell>
                                <TableCell id="active-table-head" align="left">
                                    <Box display="flex" justifyContent="center">
                                        Tickets left
                                    </Box>
                                </TableCell>
                                <TableCell>
                                        <HeadTable
                                            title={"Finish Date"}
                                            sort={(direction, field) => handleSort(direction, 'endsAt')}
                                            filterHandle={() => filterDropdown('finish')}
                                        />
                                        {
                                        dropdown.finish
                                        &&
                                            <DateFilter
                                                applyDateFilter={applyDateFilter}
                                                clearDateFilter={clearEndDateFilter}
                                                endDate={state.endDate.endDate}
                                                startDate={state.endDate.startDate}
                                                setStartDate={setEndStartDate}
                                                setEndDate={setEndEndDate}
                                            />
                                        }
                                    
                                </TableCell>
                                <TableCell id="active-table-head-actions" align="left">Status</TableCell>
                                {
                                    checkPermission(permissions, "Fixed Odds", "Update") &&
                                    <TableCell id="active-table-head" align="left">
                                        <Box display="flex" justifyContent="center">
                                            Actions
                                    </Box>
                                    </TableCell>
                                }
                            </TableRow>
                        </TableHead>
                        {state.fixedOdds ? state.fixedOdds.map((item: FixedOdds) => {
                            return (
                                <TableBody key={item.id} onClick={closeFilter}>

                                    <TableRow className="order-row">
                                        <TableCell >
                                            {item._id}
                                        </TableCell>
                                        <TableCell>
                                            {item.title}
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" justifyContent="center">
                                                {item.ticketPrice}
                                            </Box>
                                        </TableCell>
                                        <TableCell className="category-cell">
                                            <Box display="flex" justifyContent="center">
                                                {item.isActiveDiscount ? <DoneOutlineIcon /> : null}
                                            </Box>
                                        </TableCell>
                                        <TableCell >
                                            <Box display="flex" justifyContent="center">
                                                {item.maxTickets}
                                            </Box>
                                        </TableCell>
                                        <TableCell >
                                            <Box display="flex" justifyContent="center">
                                                {item.maxTickets - (item.ticketsBought || 0)}
                                            </Box>
                                        </TableCell>
                                        <TableCell>{item.endsAt ? getDate(new Date(item.endsAt)) : "---"}</TableCell>
                                        <TableCell id="status-table-head-actions">
                                            <label className="switch">
                                                <input type="checkbox" checked={item.active} onClick={() => changeStatus(item)} />
                                                <span className="slider round"></span>
                                            </label>
                                        </TableCell>
                                        {
                                            checkPermission(permissions, "Fixed Odds", "Update") &&
                                            <TableCell>
                                                <div className="actions-table-body">
                                                    <CloneButton record={item} to={`fixedOdds/${item.id}`} onClick={markClone} className="clone-prize" />
                                                    <EditButton to={`fixedOdds/${item.id}`} className="edit-prize" />
                                                    {
                                                        checkPermission(permissions, "Lifestyle Prizes", "Delete") &&
                                                        <div onClick={() => openModal(item._id)}>
                                                            <DeleteForeverIcon className="delete-icon" />
                                                        </div>
                                                    }
                                                </div>
                                            </TableCell>
                                        }
                                    </TableRow>
                                </TableBody>
                            )
                        }) : null}
                    </Table>
                </TableContainer>
                <PaginationTable
                    page={state.page}
                    perPage={state.perPage}
                    collectionLength={state.fixedOdds.length}
                    total={state.total}
                    handleChangePage={handleChangePage}
                    handleLastPage={handleLastPage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    handleFirstPage={handleFirstPage}
                    title="Fixed Odds"
                />
                <Dialog
                    fullWidth
                    open={modal.showModal}
                    onClose={closeModal}
                >
                    <DialogContent>
                        <RemoveItem closeModal={closeModal} removeUser={removeFixedOdds} mainText="FixedOdds" minorText="All information about the fixedOdds will be completely remove
                    from the system" />
                    </DialogContent>
                </Dialog>
            </Card>
        </>
    )
}
export default FixedOddsList